import { getSettings } from './Settings';
import { getSoundSystem } from './SoundSystem';
import { TrackDef, MusicNote, getTrackForLevel, MENU_TRACK } from '../config/music';

export class MusicPlayer {
  private ctx: AudioContext;
  private musicGain: GainNode;
  private filter: BiquadFilterNode;
  private source: AudioBufferSourceNode | null = null;
  private currentLevel = -1;
  private renderedBuffers: Map<number, AudioBuffer> = new Map();
  private isDucked = false;
  private duckTimeout: number | null = null;
  private fadeInterval: number | null = null;

  constructor() {
    this.ctx = getSoundSystem().getContext();
    this.musicGain = this.ctx.createGain();
    this.musicGain.gain.value = 0;

    this.filter = this.ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.frequency.value = 400;
    this.filter.Q.value = 0.7;

    this.musicGain.connect(this.filter);
    this.filter.connect(this.ctx.destination);

    getSoundSystem().setDuckCallback(() => this.duck());
  }

  async start(level: number): Promise<void> {
    if (!getSettings().musicEnabled) return;

    if (this.currentLevel === level && this.source) return;

    this.stop();
    this.currentLevel = level;

    const buffer = await this.getOrRenderBuffer(level);
    if (!getSettings().musicEnabled) return;

    this.source = this.ctx.createBufferSource();
    this.source.buffer = buffer;
    this.source.loop = true;
    this.source.connect(this.musicGain);
    this.source.start();

    this.fadeIn();
  }

  async startMenu(): Promise<void> {
    if (!getSettings().musicEnabled) return;

    const MENU_LEVEL = 0;
    if (this.currentLevel === MENU_LEVEL && this.source) return;

    this.stop();
    this.currentLevel = MENU_LEVEL;

    const cached = this.renderedBuffers.get(-1);
    const buffer = cached ?? await this.renderTrack(MENU_TRACK);
    if (!cached) this.renderedBuffers.set(-1, buffer);
    if (!getSettings().musicEnabled) return;

    this.source = this.ctx.createBufferSource();
    this.source.buffer = buffer;
    this.source.loop = true;
    this.source.connect(this.musicGain);
    this.source.start();

    this.fadeIn();
  }

  stop(): void {
    if (this.fadeInterval !== null) {
      clearInterval(this.fadeInterval);
      this.fadeInterval = null;
    }
    if (this.source) {
      try {
        this.source.stop();
      } catch { /* already stopped */ }
      this.source.disconnect();
      this.source = null;
    }
    this.musicGain.gain.value = 0;
    this.currentLevel = -1;
  }

  pause(): void {
    if (!this.source) return;
    this.fadeOut();
  }

  resume(): void {
    if (!this.source || !getSettings().musicEnabled) return;
    this.fadeIn();
  }

  setHeat(percent: number): void {
    const minFreq = 400;
    const maxFreq = 8000;
    const targetFreq = minFreq + (maxFreq - minFreq) * percent;
    this.filter.frequency.setTargetAtTime(targetFreq, this.ctx.currentTime, 0.3);
  }

  duck(): void {
    if (!this.source || this.isDucked) return;
    this.isDucked = true;
    this.musicGain.gain.setTargetAtTime(
      this.musicGain.gain.value * 0.3,
      this.ctx.currentTime,
      0.05,
    );

    if (this.duckTimeout !== null) {
      clearTimeout(this.duckTimeout);
    }
    this.duckTimeout = window.setTimeout(() => {
      this.isDucked = false;
      if (this.source && getSettings().musicEnabled) {
        this.musicGain.gain.setTargetAtTime(0.25, this.ctx.currentTime, 0.2);
      }
      this.duckTimeout = null;
    }, 500);
  }

  onSettingsChanged(): void {
    if (getSettings().musicEnabled) {
      if (this.currentLevel > 0 && !this.source) {
        const level = this.currentLevel;
        this.start(level);
      } else if (this.currentLevel <= 0 && !this.source) {
        this.startMenu();
      } else if (this.source) {
        this.fadeIn();
      }
    } else {
      const level = this.currentLevel;
      this.stop();
      this.currentLevel = level;
    }
  }

  private fadeIn(): void {
    if (this.fadeInterval !== null) {
      clearInterval(this.fadeInterval);
    }
    const target = 0.25;
    const step = 0.01;
    this.fadeInterval = window.setInterval(() => {
      const current = this.musicGain.gain.value;
      if (current >= target) {
        this.musicGain.gain.value = target;
        if (this.fadeInterval !== null) {
          clearInterval(this.fadeInterval);
          this.fadeInterval = null;
        }
        return;
      }
      this.musicGain.gain.value = Math.min(current + step, target);
    }, 60);
  }

  private fadeOut(targetVol = 0): void {
    if (this.fadeInterval !== null) {
      clearInterval(this.fadeInterval);
    }
    const step = 0.015;
    this.fadeInterval = window.setInterval(() => {
      const current = this.musicGain.gain.value;
      if (current <= targetVol + 0.001) {
        this.musicGain.gain.value = targetVol;
        if (this.fadeInterval !== null) {
          clearInterval(this.fadeInterval);
          this.fadeInterval = null;
        }
        return;
      }
      this.musicGain.gain.value = Math.max(current - step, targetVol);
    }, 60);
  }

  private async getOrRenderBuffer(level: number): Promise<AudioBuffer> {
    const biomeIndex = (level - 1) % 5;
    const cached = this.renderedBuffers.get(biomeIndex);
    if (cached) return cached;

    const track = getTrackForLevel(level);
    const buffer = await this.renderTrack(track);
    this.renderedBuffers.set(biomeIndex, buffer);
    return buffer;
  }

  private async renderTrack(track: TrackDef): Promise<AudioBuffer> {
    const secondsPerBeat = 60 / track.bpm;
    const duration = track.beatsPerLoop * secondsPerBeat;
    const sampleRate = this.ctx.sampleRate;
    const offline = new OfflineAudioContext(2, Math.ceil(duration * sampleRate), sampleRate);

    const masterGain = offline.createGain();
    masterGain.gain.value = 1;
    masterGain.connect(offline.destination);

    // Highpass filter on bass to cut sub-frequencies that cause speaker buzz
    const bassFilter = offline.createBiquadFilter();
    bassFilter.type = 'highpass';
    bassFilter.frequency.value = 200;
    bassFilter.connect(masterGain);

    this.renderBass(offline, bassFilter, track.bass, secondsPerBeat);
    this.renderPad(offline, masterGain, track.pad, secondsPerBeat);
    this.renderMelody(offline, masterGain, track.melody, secondsPerBeat);

    return offline.startRendering();
  }

  private renderBass(
    ctx: OfflineAudioContext,
    dest: AudioNode,
    notes: MusicNote[],
    spb: number,
  ): void {
    for (const note of notes) {
      const start = note.beat * spb;
      const dur = note.dur * spb;
      const vol = note.vel * 0.6;

      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = note.freq;

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(vol, start + 0.02);
      gain.gain.setValueAtTime(vol, start + dur * 0.7);
      gain.gain.exponentialRampToValueAtTime(0.001, start + dur);

      osc.connect(gain);
      gain.connect(dest);
      osc.start(start);
      osc.stop(start + dur + 0.05);
    }
  }

  private renderPad(
    ctx: OfflineAudioContext,
    dest: GainNode,
    notes: MusicNote[],
    spb: number,
  ): void {
    for (const note of notes) {
      const start = note.beat * spb;
      const dur = note.dur * spb;
      const vol = note.vel * 0.5;

      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = note.freq;

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(vol, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(vol * 0.4, start + 0.15);
      gain.gain.exponentialRampToValueAtTime(0.001, start + dur * 0.5);

      osc.connect(gain);
      gain.connect(dest);
      osc.start(start);
      osc.stop(start + dur * 0.5 + 0.05);
    }
  }

  private renderMelody(
    ctx: OfflineAudioContext,
    dest: GainNode,
    notes: MusicNote[],
    spb: number,
  ): void {
    for (const note of notes) {
      const start = note.beat * spb;
      const dur = note.dur * spb;

      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = note.freq;

      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 4.5;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 2;
      lfo.connect(lfoGain);
      lfoGain.connect(osc.frequency);

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(note.vel, start + 0.03);
      gain.gain.setValueAtTime(note.vel, start + dur * 0.6);
      gain.gain.exponentialRampToValueAtTime(0.001, start + dur);

      osc.connect(gain);
      gain.connect(dest);
      osc.start(start);
      osc.stop(start + dur);
      lfo.start(start);
      lfo.stop(start + dur);
    }
  }
}

let instance: MusicPlayer | null = null;

export function getMusicPlayer(): MusicPlayer {
  if (!instance) {
    instance = new MusicPlayer();
  }
  return instance;
}
